// ─────────────────────────────────────────────────────────────────────────────
// MobileSheet.tsx — Generic full-screen slide-up sheet shell (mobile only)
//
// Used by all three mobile bottom-sheets (Pages / Customize / More). Handles:
//  - backdrop + slide-up panel animation
//  - body scroll lock while open (useBodyScrollLock)
//  - sticky header with title + close button
//  - dismiss on backdrop tap or close button
//
// Content components (mobileSheetContent/*) only render what goes *inside*.
// ─────────────────────────────────────────────────────────────────────────────

import { AnimatePresence, motion } from 'framer-motion';
import { X } from 'lucide-react';
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock';

interface MobileSheetProps {
  isOpen: boolean;
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}

export function MobileSheet({ isOpen, title, onClose, children }: MobileSheetProps) {
  useBodyScrollLock(isOpen);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            className="fixed inset-0 z-[60] bg-black/50 md:hidden"
            aria-hidden="true"
          />

          {/* Sheet panel */}
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed inset-x-0 bottom-0 z-[61] h-[88dvh] max-h-[88dvh] rounded-t-2xl
                       flex flex-col md:hidden"
            style={{
              // Was hardcoded bg-white dark:bg-navy-900 — now follows the
              // active colour scheme. translateZ forces its own compositing
              // layer, reducing jank while Chrome's toolbar shows/hides.
              backgroundColor: 'var(--color-surface)',
              transform: 'translateZ(0)',
            }}
            role="dialog"
            aria-modal="true"
            aria-label={title}
          >
            {/* Sticky header */}
            <div
              className="flex-shrink-0 flex items-center justify-between px-5 py-4 border-b"
              style={{ borderColor: 'var(--color-border)' }}
            >
              <h2 className="font-display font-bold text-base" style={{ color: 'var(--color-text)' }}>
                {title}
              </h2>
              <button
                onClick={onClose}
                className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-navy-800 text-slate-500 dark:text-slate-400 transition-colors"
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>

            {/* Scrollable body */}
            <div
              className="flex-1 overflow-y-auto overscroll-contain px-2 py-2"
              style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 10px)' }}
            >
              {children}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
